import React, { useCallback, useRef, useState } from "react";
import { COLORS, FONTS } from "@/constants/theme";

const joinClasses = (...values) => values.filter(Boolean).join(" ");

const VARIANT_STYLES = {
  primary: {
    backgroundColor: COLORS.digitalBlue,
    border: `1px solid ${COLORS.digitalBlue}`,
    color: COLORS.textPrimary,
  },
  outline: {
    backgroundColor: "transparent",
    border: `1px solid ${COLORS.borderMedium}`,
    color: COLORS.textPrimary,
  },
};

const HOVER_STYLES = {
  primary: {
    backgroundColor: COLORS.bloombergBlue,
    borderColor: COLORS.bloombergBlue,
    boxShadow: "0 10px 30px rgba(0,104,255,0.34)",
  },
  outline: {
    backgroundColor: COLORS.bgTertiary,
    borderColor: COLORS.borderStrong,
  },
};

/**
 * Botón CTA reutilizable. Además del estado de hover, deja una onda al pulsar
 * para que la acción se sienta respondida también al tocar, no solo al pasar
 * el cursor.
 *
 * @param {{
 *   children: React.ReactNode,
 *   href?: string,
 *   onClick?: React.MouseEventHandler<HTMLElement>,
 *   variant?: "primary" | "outline",
 *   className?: string,
 *   type?: "button" | "submit" | "reset",
 *   fullWidth?: boolean,
 *   style?: React.CSSProperties
 * }} props
 * @returns {JSX.Element}
 */
const CTAButton = ({
  children,
  href,
  onClick,
  variant = "primary",
  className = "",
  type = "button",
  fullWidth = false,
  style = {},
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [pulsado, setPulsado] = useState(false);
  const nodoRef = useRef(null);
  const Component = href ? "a" : "button";

  const lanzarOnda = useCallback(
    (evento) => {
      const nodo = nodoRef.current;
      if (nodo && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        const caja = nodo.getBoundingClientRect();
        // Al activar con el teclado no hay coordenadas (llegan en 0): la onda
        // sale entonces del centro del botón en vez de la esquina.
        const conPuntero = evento.clientX !== 0 || evento.clientY !== 0;
        const x = conPuntero ? evento.clientX - caja.left : caja.width / 2;
        const y = conPuntero ? evento.clientY - caja.top : caja.height / 2;

        const onda = document.createElement("span");
        onda.className = "onda";
        onda.style.left = `${x}px`;
        onda.style.top = `${y}px`;
        onda.addEventListener("animationend", () => onda.remove());
        nodo.appendChild(onda);
      }

      if (onClick) {
        onClick(evento);
      }
    },
    [onClick],
  );

  const baseStyle = {
    position: "relative",
    overflow: "hidden",
    fontFamily: FONTS.heading,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderRadius: "4px",
    padding: "0.9rem 1.2rem",
    fontSize: "0.9rem",
    lineHeight: 1.2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    width: fullWidth ? "100%" : "auto",
    transition: "background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease, transform 120ms ease",
    textDecoration: "none",
    cursor: "pointer",
    transform: pulsado ? "translateY(1px) scale(0.985)" : "translateY(0) scale(1)",
    ...VARIANT_STYLES[variant],
    ...(isHovering ? HOVER_STYLES[variant] : {}),
    ...style,
  };

  return (
    <Component
      className={joinClasses("min-h-[50px]", className)}
      href={href}
      onBlur={() => setPulsado(false)}
      onClick={lanzarOnda}
      onMouseDown={() => setPulsado(true)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setPulsado(false);
      }}
      onMouseUp={() => setPulsado(false)}
      onTouchEnd={() => setPulsado(false)}
      onTouchStart={() => setPulsado(true)}
      ref={nodoRef}
      style={baseStyle}
      type={href ? undefined : type}
    >
      {children}
    </Component>
  );
};

export default CTAButton;
